import { Grid, TextField } from "@mui/material";
import { useState } from "react";
import * as React from "react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import Stack from "@mui/joy/Stack";
import Button from "@mui/joy/Button";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import { LightTooltip } from "../tooltip/ToolTip";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

const AddSpeciality = (props) => {
  const { closeDialog } = props;
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const resetForm = (props) => {
    // clear input fields
    var now = new Date();
    props.resetForm();
    setSelectedSpeciality("");
    setSelectedSpecialityKey(now);
  };

  const closeForm = (message) => {
    closeDialog(message);
  };

  // Specialities
  const [specialitiesOpen, setSpecialitiesOpen] = useState(false);
  const [specialities, setSpecialities] = useState([]);
  const [specialityLoading, setSpecialityLoading] = useState(
    specialitiesOpen && specialities.length === 0
  );
  const [selectedSpeciality, setSelectedSpeciality] = useState("");
  const [selectedSpecialityKey, setSelectedSpecialityKey] =
    useState("specialityKey");
  const [specialityEmpty, setSpecialityEmpty] = useState(false);
  const SPECIALITIES_URL = "api/v1/specialities";
  const handleSpecialityChange = (event, value) => {
    setSelectedSpeciality(value.id);
    setSpecialityEmpty(false);
  };

  React.useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (!specialityLoading) {
      return undefined;
    }

    const getSpecialities = async () => {
      try {
        const response = await axiosPrivate.get(SPECIALITIES_URL, {
          signal: controller.signal,
        });
        isMounted && setSpecialities(response.data.data);
        setSpecialityLoading(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setSpecialityLoading(false);
      }
    };

    getSpecialities();

    return () => {
      isMounted = false;
      controller.abort();
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [specialityLoading]);

  React.useEffect(() => {
    if (!specialitiesOpen) {
      setSpecialities([]);
    }
  }, [specialitiesOpen]);

  // Validate form
  const validationSchema = Yup.object().shape({});

  // Common
  const initialValues = {
    specialityId: selectedSpeciality,
    tenantId: auth.tenantId,
  };

  function onSubmit(values, props) {
    // No speciality picked
    if (selectedSpeciality === "") {
      setSpecialityEmpty(true);
      props.setSubmitting(false);
      return;
    }

    props.setSubmitting(true);
    addSpeciality(values, props);
  }

  const TENANT_URL = "/api/v1/tenants/add-speciality";
  const addSpeciality = async (values, props) => {
    // Request payload
    const data = {
      specialityId: selectedSpeciality,
      tenantId: auth.tenantId,
    };

    try {
      const response = await axiosPrivate.post(TENANT_URL, data, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      //console.log(response.data.message);
      props.setSubmitting(false);
      resetForm(props);
      closeForm(response.data.message);
    } catch (error) {
      props.setSubmitting(false);
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: `Failed To Add Speciality`,
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: { onSubmit },
      });
    }
  };

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  return (
    <Grid>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(props) => (
          <Form>
            <Stack spacing={2} mt={3} direction="column">
              <Autocomplete
                id="speaciality"
                fullWidth
                onChange={handleSpecialityChange}
                key={selectedSpecialityKey}
                open={specialitiesOpen}
                onOpen={() => {
                  setSpecialityLoading(true);
                  setSpecialitiesOpen(true);
                }}
                onClose={() => {
                  setSpecialityLoading(false);
                  setSpecialitiesOpen(false);
                }}
                isOptionEqualToValue={(speciality, value) =>
                  speciality.name === value.name
                }
                getOptionLabel={(speciality) =>
                  speciality.name ? speciality.name : ""
                }
                options={specialities}
                loading={specialityLoading}
                loadingText="Loading specialities..."
                noOptionsText="No specialities"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Specialiaty"
                    name="speciality"
                    error={specialityEmpty}
                    helperText={specialityEmpty ? "Speciality required" : ""}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {specialityLoading ? (
                            <CircularProgress color="primary" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Stack>

            <Stack spacing={2} mt={3} direction="row" justifyContent="flex-end">
              <LightTooltip title="Clear form">
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: 8,
                    width: 120,
                    backgroundColor: "#808080",
                    padding: "7px 14px",
                    fontSize: "14px",
                    color: "white",
                    ":hover": {
                      bgcolor: "#696969",
                      color: "white",
                    },
                  }}
                  onClick={() => {
                    resetForm(props);
                  }}
                >
                  Reset
                </Button>
              </LightTooltip>

              <LightTooltip title="Submit details">
                <Button
                  loading={props.isSubmitting}
                  loadingPosition="start"
                  disabled={props.isSubmitting}
                  type="submit"
                  sx={{
                    width: 120,
                  }}
                  variant="solid"
                >
                  {props.isSubmitting ? "Submitting" : "Submit"}
                </Button>
              </LightTooltip>
            </Stack>
          </Form>
        )}
      </Formik>

      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </Grid>
  );
};

export default AddSpeciality;
